/**
 * S-corp formation-readiness soft-warn card on Dashboard.
 *
 * NS4 R7 — closes audit Lens A #3. For S-corp entities only:
 * verify the 4 foundational policy documents are signed + counsel-reviewed
 * before declaring the entity ready for full operation.
 *
 * Soft-warn only — never blocks any feature. Surfaces only when there's
 * actually a gap; renders nothing when status='ready'.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, ShieldCheck, FileText, AlertCircle, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useAppStore } from '@/stores/useAppStore';
import { useRole } from '@/hooks/useRole';
import { api, isInLocalMode } from '@/lib/api';

type Readiness = Awaited<ReturnType<typeof api.getEntityReadiness>>;

export function FormationReadinessCard() {
  const { activeEntity } = useAppStore();
  const { hasMinRole } = useRole();
  const [data, setData] = useState<Readiness | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Bookkeeper+ can read (matches backend ensureFinanceReadAccess).
  const canRead = hasMinRole('bookkeeper');

  useEffect(() => {
    if (isInLocalMode() || !activeEntity?.id || !canRead) { setLoaded(true); return; }
    api.getEntityReadiness()
      .then((r) => setData(r))
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, [activeEntity?.id, canRead]);

  if (!loaded || !data) return null;

  // Not an S-corp — gate doesn't apply, render nothing.
  if (!data.applicable) return null;

  // All checks green — no soft-warn needed.
  if (data.overallStatus === 'ready') return null;

  const { summary, checks, overallStatus } = data;
  if (!summary || !checks) return null;

  const required = checks.filter((c) => c.required);

  // Color theme based on severity
  const isBlocked = overallStatus === 'blocked';
  const iconColor = isBlocked
    ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
    : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400';
  const borderColor = isBlocked
    ? 'border-red-200/60 dark:border-red-900/40'
    : 'border-amber-200/60 dark:border-amber-900/40';

  const headlineText = isBlocked
    ? 'S-corp formation incomplete'
    : overallStatus === 'overdue_review'
      ? 'S-corp documents need counsel review'
      : 'S-corp documents pending counsel review';

  const subline = isBlocked
    ? `${summary.missing + summary.unsigned} of ${summary.total} foundational documents missing or unsigned. IRS may treat compensation as disguised wages.`
    : overallStatus === 'overdue_review'
      ? `${summary.counselReviewOverdue} document${summary.counselReviewOverdue === 1 ? '' : 's'} past the 12-month review cadence.`
      : `${summary.pendingCounselReview} document${summary.pendingCounselReview === 1 ? '' : 's'} need attorney/CPA sign-off for audit defense.`;

  return (
    <Card padding="md" className={borderColor}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${iconColor}`}>
          {isBlocked ? <ShieldAlert className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            {headlineText}
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            {subline}
          </p>

          <div className="mt-3 space-y-1.5">
            {required.map((check) => {
              const statusLabel = {
                ready: 'Ready',
                missing: 'Missing',
                unsigned: 'Unsigned',
                pending_counsel_review: 'Needs counsel review',
                counsel_review_overdue: 'Review overdue',
                not_applicable: 'N/A',
              }[check.status];
              const badgeVariant = check.status === 'ready'
                ? 'approved' as const
                : check.status === 'pending_counsel_review' || check.status === 'unsigned'
                  ? 'pending' as const
                  : 'flagged' as const;
              const Icon = check.status === 'ready' ? FileText : AlertCircle;
              return (
                <div
                  key={check.docType}
                  className="flex items-center gap-2 text-xs py-1.5 px-2 rounded bg-white/60 dark:bg-slate-800/40"
                >
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${check.status === 'ready' ? 'text-emerald-600' : 'text-amber-600'}`} />
                  <span className="flex-1 min-w-0 text-slate-700 dark:text-slate-300 truncate">{check.label}</span>
                  <Badge variant={badgeVariant}>{statusLabel}</Badge>
                </div>
              );
            })}
          </div>

          <Link
            to="/documents"
            className="mt-3 text-xs text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            Manage policy documents <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </Card>
  );
}
